import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import {
	clearCachedMessages,
	removeCachedMessage
} from '../../messages/messageSlice';
import { formatDateToLocalDateTime } from '../../util/DateUtil.js';

import { Notification } from '@progress/kendo-react-notification';
import {
	ListView,
	ListViewHeader
} from "@progress/kendo-react-listview";
import { Button } from '@progress/kendo-react-buttons';

export const setNotificationCloseButtonAccessibility = (container) => {
	const closeButtons = container?.querySelectorAll(
		'.k-notification-close-action'
	) || [];

	closeButtons.forEach((button) => {
		button.setAttribute("tabIndex", "0");
		button.setAttribute("role", "button");
		button.setAttribute(
			"aria-label",
			"Clear Notification"
		);
	});
};

export default function CachedMessages(props) {

	const { closeNotifications } = props;
	const cachedMessages = useSelector(
		(state) => state.messages.cachedMessages
	).filter((msg) => !msg.hidden);

	const dispatch = useDispatch();
	const [messages, setMessages] = useState(cachedMessages);
	const cachedMessagesRef = useRef(null);

	useEffect(() => {
		setNotificationCloseButtonAccessibility(cachedMessagesRef.current);
	}, [messages]);

	const cachedMessagesKeyDown = (e) => {
		const closeButton = e.target.closest?.(
			'.k-notification-close-action'
		);

		if (
			!closeButton ||
			(e.key !== "Enter" && e.key !== " ")
		) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();
		closeButton.click();
	};

	const clearMessages = () => {
		dispatch(clearCachedMessages());
		setMessages([]);
		closeNotifications?.();
	};

	const header = () => {
		return (
			<ListViewHeader className="constituo-cached-messages-header">
				<div className="constituo-cached-messages-message-leftside">
					<div
						style={{
							lineHeight: "36px",
							fontSize: "12pt"
						}}
					>
						Recent Notifications
					</div>
				</div>

				<div className="constituo-cached-messages-message-rightside">
					<span
						style={{ lineHeight: "36px" }}
						onKeyDown={(e) => {
							if (
								e.key !== "Enter" &&
								e.key !== " "
							) {
								return;
							}

							e.preventDefault();
							e.stopPropagation();
							clearMessages();
						}}
					>
						<Button
							title="Clear Notifications"
							iconClass="fa fa-fw fa-cancel"
							onClick={(e) => {
								e?.preventDefault?.();
								e?.stopPropagation?.();
								e?.syntheticEvent?.preventDefault?.();
								e?.syntheticEvent?.stopPropagation?.();
								clearMessages();
							}}
						/>
					</span>
				</div>
			</ListViewHeader>
		);
	};

	const renderNotificationMessage = (item) => {
		return (
			<div
				className="constituo-cached-messages-message-container"
				tabIndex={0}
				role="group"
				aria-label={`Notification: ${item.msg}`}
			>
				<div className="constituo-cached-messages-message-leftside">
					<div className="constituo-cached-messages-msg">
						{item.msg}
					</div>

					<div className="constituo-cached-messages-timestamp">
						{formatDateToLocalDateTime(
							null,
							item.timestamp
						)}
					</div>
				</div>
			</div>
		);
	};

	const removeMessage = (idx) => {
		dispatch(removeCachedMessage(idx));

		const newMsgs = [];

		for (let i = 0; i < messages.length; i++) {
			if (messages[i].msgIdx != idx) {
				newMsgs.push(messages[i]);
			}
		}

		setMessages(newMsgs);
	};

	const render = (renderProps) => {
		const item = renderProps.dataItem;

		return (
			<Notification
				type={{
					icon: true,
					style: item.type
				}}
				className="k-notification-cached"
				style={{ display: "inline-flex" }}
				closable={true}
				onClose={(e) => {
					e?.syntheticEvent?.preventDefault?.();
					e?.syntheticEvent?.stopPropagation?.();
					e?.nativeEvent?.preventDefault?.();
					e?.nativeEvent?.stopPropagation?.();

					removeMessage(item.msgIdx);
				}}
			>
				{renderNotificationMessage(item)}
			</Notification>
		);
	};

	return (
		<div
			ref={cachedMessagesRef}
			tabIndex={0}
			role="region"
			aria-label="Recent Notifications Panel"
			className="constituo-cached-messages-container"
			onKeyDown={cachedMessagesKeyDown}
		>
			<ListView
				tabIndex={0}
				data={messages}
				item={render}
				style={{
					width: "360px",
					height: "100%"
				}}
				header={header}
			/>
		</div>
	);
}
